import Clarity from "@microsoft/clarity";

/**
 * The only module that imports `@microsoft/clarity` and touches `window.clarity`.
 *
 * Why the official package instead of the inline snippet: it owns the loader (one script element,
 * deduplicated by id) and gives the calls that matter here — `consentV2`, `identify`, `setTag`,
 * `upgrade` — a typed surface, so a typo in a call name is a compile error instead of a silent
 * no-op in the queue. It has no dependencies and ships ESM.
 *
 * Why the guard on every call but `startClarity`: the package calls `window.clarity(...)` directly
 * and throws a `TypeError` when `init` has not run (pending or refused consent, no project
 * configured, server). Analytics must never break the page, so each wrapper does nothing until the
 * stub exists and swallows whatever the package raises, in the pattern of `data-layer.ts`.
 *
 * Why `ad_Storage: "denied"`: the accept the visitor gives covers "cookies and monitoring", and
 * the Privacy Policy's section 06 lists session recording, heat maps and performance — not
 * advertising. The session cookies (`_clck`/`_clsk`) are analytical, so they stay allowed.
 * Measured on 2026-10-22: with the ad signal denied, Clarity still calls `c.bing.com` once per
 * visit to sync a `MUID` cookie, so the signal declares the purpose of the accept rather than
 * silencing that request. Sending it explicitly matters even outside the regions where Consent
 * Mode is mandatory: without it a project that enters Consent Mode splits every page view into a
 * new session.
 *
 * Identification (`identifyClarityVisitor`, `tagClaritySession`) carries the visitor's email in
 * plain text. That is a deliberate, product-owner-approved exception to the project's "no PII in
 * analytics" rule, valid for Clarity identification only; `dataLayer`/GTM stay free of PII.
 */

/** Clarity project ids are short alphanumeric tokens; anything else is not an id. */
const PROJECT_ID_PATTERN = /^[a-z0-9]+$/i;

/** Id of the script element the package injects; used to tell whether a start already happened. */
const CLARITY_SCRIPT_ID = "clarity-script";

/** What the visitor's accept grants Clarity. See the docblock above for the reasoning. */
export const CLARITY_CONSENT = {
  ad_Storage: "denied",
  analytics_Storage: "granted",
} as const;

/** The configured project id, or `null` when absent or not shaped like an id. */
export function readClarityProjectId(): string | null {
  const projectId = process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID;

  return projectId && PROJECT_ID_PATTERN.test(projectId) ? projectId : null;
}

/**
 * Loads Clarity and sends the consent signal. Idempotent: the early entry
 * (`instrumentation-client.ts`) and the container may hold different module instances, so a module
 * flag would not be shared between them. The page is: `init` inserts the script element
 * synchronously, and its presence is the one fact both entries can see.
 *
 * Callers are responsible for the accept — this function does not read the consent.
 */
export function startClarity(projectId: string): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (document.getElementById(CLARITY_SCRIPT_ID)) return;

  try {
    Clarity.init(projectId);
    // Queued by the stub `init` installs, and replayed when the tag boots.
    Clarity.consentV2(CLARITY_CONSENT);
  } catch {
    // Analytics is best-effort and must never alter the page.
  }
}

function clarityIsReady(): boolean {
  return typeof window !== "undefined" && typeof window.clarity === "function";
}

/** Ties the session to a person; the id is hashed by Clarity, the friendly name is not. */
export function identifyClarityVisitor(customId: string, friendlyName: string): void {
  if (!clarityIsReady()) return;

  try {
    Clarity.identify(customId, undefined, undefined, friendlyName);
  } catch {
    // See `startClarity`.
  }
}

/** Sets custom tags on the session, each as a filterable key in the dashboard. */
export function tagClaritySession(tags: Record<string, string>): void {
  if (!clarityIsReady()) return;

  for (const [key, value] of Object.entries(tags)) {
    try {
      Clarity.setTag(key, value);
    } catch {
      // One bad tag must not cost the others.
    }
  }
}

/** Asks Clarity to keep the session when traffic goes past the daily sampling. */
export function upgradeClaritySession(reason: string): void {
  if (!clarityIsReady()) return;

  try {
    Clarity.upgrade(reason);
  } catch {
    // See `startClarity`.
  }
}

declare global {
  interface Window {
    clarity?: unknown;
  }
}
